import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { useState, type ReactNode } from "react";
import clsx from "clsx";
import EmptyState from "../common/empty-state/EmptyState.tsx";
import { FormattedMessage, useIntl } from "react-intl";
import DefaultTemplate from "../layout/content-templates/default/DefaultTemplate.tsx";
import PageTitle from "../common/page-title/PageTitle.tsx";
import BaseLinkOrButton from "../../../common/core-ui/BaseLinkOrButton.tsx";
import {
  getVirtualBookstoreSupportLink,
  submitVitalSourceSupport,
} from "./common/links.ts";
import useVirtualStore from "../common/virtual-store/useVirtualStore.tsx";
import { SignInPromptData } from "./noCoursesResponse.ts";
import { useBrandLoaderData } from "../../root/rootLoader.ts";
import styles from "./MyCoursesError.module.scss";
import MiscellaneousSearch from "common/icons/MiscellaneousSearch.tsx";
import BookshelfBooks from "common/icons/BookshelfBooks.tsx";
import CoursePickerDialog from "../common/course-picker/CoursePickerDialog.tsx";

function Graphic() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="441"
      height="223"
      viewBox="0 0 441 223"
      fill="none"
      aria-hidden={true}
      focusable={false}
    >
      <g clipPath="url(#courses-error)">
        <path
          fill="#D0D7EB"
          d="M100 101h19v72.428c0 1.972-2.304 3.572-5.146 3.572h-8.698c-2.842 0-5.146-1.6-5.146-3.572V101H100ZM361.573 177h-9.156c-2.992 0-5.417-1.6-5.417-3.572V101h20v72.428c0 1.972-2.425 3.572-5.417 3.572h-.01Z"
        />
        <path
          stroke="#8FA1D2"
          strokeLinecap="round"
          strokeMiterlimit="10"
          strokeWidth="3"
          d="M383.67 48.45h15.29"
        />
        <path
          stroke="#425B9F"
          strokeLinecap="round"
          strokeMiterlimit="10"
          strokeWidth="3"
          d="M438.67 77.69V55.61c0-3.95-3.2-7.16-7.16-7.16h-21.99M1 62.62h23.58M11.48 36.79l16.68 16.68M37.06 25v23.58"
        />
        <path
          fill="#D2D9ED"
          fillOpacity=".4"
          d="M55 101h20v109.015c0 2.201-2.425 3.985-5.417 3.985h-9.156c-2.991 0-5.417-1.784-5.417-3.985V101H55ZM405.573 214h-9.156c-2.992 0-5.417-1.784-5.417-3.985V101h20v109.015c0 2.201-2.425 3.985-5.417 3.985h-.01Z"
        />
        <path
          stroke="#8FA1D2"
          strokeLinecap="round"
          strokeMiterlimit="10"
          strokeWidth="3"
          d="m315.87 4.65-4.09 11.7"
        />
        <path
          fill="#425B9F"
          d="M296.82 0a.83.83 0 0 0-.9.84c.01 2.07.41 7.34 3.8 10.88 2.65 2.75 6.37 4.78 8.14 5.66a.826.826 0 0 0 1.19-.63c.33-2.24.9-7.29-.18-9.73C306.34 1.25 299.31.2 296.82 0Z"
        />
        <path
          fill="#3BBAE3"
          d="M331.9 33.4c0 9.83-8.62 23.17-15.46 23.17-4.48 0-2.76-.76-7.17-.76-4.03 0-1.57.76-7.06.67-4.26-.07-16.47-9.86-17.03-20.01-1.43-25.89 17.7-20.54 24.08-20.54 5.04 0 22.63-4.96 22.63 17.47h.01Z"
        />
        <path
          fill="#fff"
          d="M291.58 40.79c.27.56 1.12.36 1.11-.26-.04-3.36.12-9.46 1.68-13.48 1.14-2.95 3.76-5.14 5.64-6.41.51-.35.22-1.14-.4-1.07-2.59.3-6.46 1.15-7.97 3.52-4.3 6.74-1.78 14.13-.06 17.7Z"
        />
        <path
          fill="#D0D7EB"
          d="m94.92 54.29-.04-22.28c0-1.8.91-3.25 2.05-3.26l125.15-.35c1.13 0 2.05 1.45 2.05 3.24l.04 22.28c0 1.8-.91 3.25-2.05 3.26l-125.15.35c-1.13 0-2.05-1.45-2.05-3.24Z"
        />
        <path
          fill="#F2F4F9"
          d="M175.01 51.4c-.78 0-1.42-.55-1.42-1.23l-.03-14.39c0-.68.63-1.23 1.42-1.23.78 0 1.42.55 1.42 1.23l.03 14.39c0 .68-.63 1.23-1.42 1.23ZM209.11 51.4c-.78 0-1.42-.55-1.42-1.23l-.04-14.39c0-.68.63-1.23 1.42-1.23.78 0 1.42.55 1.42 1.23l.04 14.39c0 .68-.63 1.23-1.42 1.23ZM160.08 51.4c-.78 0-1.42-.55-1.42-1.23l-.03-14.39c0-.68.63-1.23 1.42-1.23.78 0 1.42.55 1.42 1.23l.03 14.39c0 .68-.63 1.23-1.42 1.23Z"
        />
        <path
          fill="#D2D9ED"
          fillOpacity=".4"
          d="M233.34 23.3V10.25a4.98 4.98 0 0 0-4.98-4.98H105.53a4.98 4.98 0 0 0-4.98 4.98V23.3a4.98 4.98 0 0 0 4.98 4.98h122.83a4.98 4.98 0 0 0 4.98-4.98Z"
        />
        <path
          fill="#D0D7EB"
          d="M222.53 23.18c-.79 0-1.42-.55-1.42-1.23V11.6c0-.68.64-1.23 1.42-1.23.78 0 1.42.55 1.42 1.23v10.35c0 .68-.64 1.23-1.42 1.23ZM207.66 16.79v-.01a4.79 4.79 0 0 0-4.79-4.79h-52.81a4.79 4.79 0 0 0-4.79 4.79v.01a4.79 4.79 0 0 0 4.79 4.79h52.81a4.79 4.79 0 0 0 4.79-4.79Z"
        />
        <path
          fill="#D2D9ED"
          fillOpacity=".4"
          d="M423.04 57.29H39.25A6.25 6.25 0 0 0 33 63.54v31.21a6.25 6.25 0 0 0 6.25 6.25h383.79a6.25 6.25 0 0 0 6.25-6.25V63.54a6.25 6.25 0 0 0-6.25-6.25Z"
        />
      </g>
      <defs>
        <clipPath id="courses-error">
          <path fill="#fff" d="M0 0h440.17v252.99H0z" />
        </clipPath>
      </defs>
    </svg>
  );
}

function isSignInPromptData(data: unknown): data is SignInPromptData {
  return (
    typeof data === "object" &&
    data !== null &&
    typeof (data as { signInUrl?: unknown }).signInUrl === "string"
  );
}

interface EmptyStateVariantProps {
  supportLink: (chunks: ReactNode) => ReactNode;
}

function SignInEmptyState({
  signInUrl,
  supportLink,
}: EmptyStateVariantProps & { signInUrl: string }) {
  const intl = useIntl();
  const brand = useBrandLoaderData();

  return (
    <EmptyState
      graphic={<Graphic />}
      title={intl.formatMessage({
        defaultMessage: "No Courses to Display",
      })}
      body={
        <>
          <p>
            <FormattedMessage defaultMessage="We’re unable to find active courses to show you right now." />
          </p>
          <FormattedMessage
            defaultMessage="Make sure that you’re signed in with your {brandName} account."
            values={{ brandName: brand.brand }}
            tagName="p"
          />
          <FormattedMessage
            defaultMessage="If you’re still having difficulties, please contact <a>Support</a>."
            values={{ a: supportLink }}
            tagName="p"
          />
        </>
      }
      buttonProps={{
        href: signInUrl,
        children: (
          <FormattedMessage
            defaultMessage="Sign in with {brandName}"
            values={{ brandName: brand.brand }}
          />
        ),
        target: "_self",
      }}
    />
  );
}

function GetStartedEmptyState({
  courseBrowseEnabled,
  supportLink,
}: EmptyStateVariantProps & { courseBrowseEnabled: boolean }) {
  const intl = useIntl();
  const [isCoursePickerOpen, setIsCoursePickerOpen] = useState(false);

  return (
    <EmptyState
      size="large"
      title={intl.formatMessage({
        defaultMessage: "No Courses to Display",
      })}
      body={
        <>
          <FormattedMessage defaultMessage="We couldn’t find active courses for your account. Here are a few ways to get started." />
          <div className={styles.cards}>
            <div
              className={clsx(
                "vst-card",
                "vst-card-variant-raised",
                styles.card
              )}
            >
              <h3>
                <FormattedMessage defaultMessage="Search for Materials" />
              </h3>
              <p>
                <FormattedMessage defaultMessage="Search by title, author, or ISBN to find exactly what you need." />
              </p>
              <BaseLinkOrButton
                to="/search"
                className={clsx("vst-button-pill", styles.button)}
              >
                <MiscellaneousSearch />
                <FormattedMessage defaultMessage="Search Now" />
              </BaseLinkOrButton>
            </div>
            {courseBrowseEnabled && (
              <div
                className={clsx(
                  "vst-card",
                  "vst-card-variant-raised",
                  styles.card
                )}
              >
                <h3>
                  <FormattedMessage defaultMessage="Browse by Course" />
                </h3>
                <p>
                  <FormattedMessage defaultMessage="Browse courses at your institution to view required course materials." />
                </p>
                <BaseLinkOrButton
                  onClick={() => setIsCoursePickerOpen(true)}
                  className={clsx(
                    "vst-button-pill",
                    "vst-button-variant-outline",
                    styles.button
                  )}
                >
                  <BookshelfBooks />
                  <FormattedMessage defaultMessage="Browse Courses" />
                </BaseLinkOrButton>
                {isCoursePickerOpen && (
                  <CoursePickerDialog
                    onRequestClose={() => setIsCoursePickerOpen(false)}
                  />
                )}
              </div>
            )}
          </div>
          <FormattedMessage
            defaultMessage="Have questions? Please contact <a>Support</a>."
            values={{ a: supportLink }}
          />
        </>
      }
    />
  );
}

function ContactSupportEmptyState({ supportLink }: EmptyStateVariantProps) {
  const intl = useIntl();

  return (
    <EmptyState
      graphic={<Graphic />}
      title={intl.formatMessage({
        defaultMessage: "No Courses to Display",
      })}
      body={
        <>
          <p>
            <FormattedMessage defaultMessage="We’re unable to find active courses to show you right now." />
          </p>
          <FormattedMessage
            defaultMessage="Please try again later or <a>contact Support</a>."
            values={{ a: supportLink }}
          />
        </>
      }
    />
  );
}

function NoCoursesEmptyState({
  signInUrl,
  courseBrowseEnabled,
  supportLink,
}: EmptyStateVariantProps & {
  signInUrl: string | null;
  courseBrowseEnabled: boolean;
}) {
  const brand = useBrandLoaderData();

  if (signInUrl !== null) {
    return <SignInEmptyState signInUrl={signInUrl} supportLink={supportLink} />;
  }
  if (brand.Search.enabled) {
    return (
      <GetStartedEmptyState
        courseBrowseEnabled={courseBrowseEnabled}
        supportLink={supportLink}
      />
    );
  }
  return <ContactSupportEmptyState supportLink={supportLink} />;
}

export default function MyCoursesError() {
  const error = useRouteError();
  const intl = useIntl();
  const title = intl.formatMessage({ defaultMessage: "My Courses" });
  const { isVirtualStore } = useVirtualStore();

  if (isRouteErrorResponse(error) && error.status === 404) {
    const institutionSignInUrl = isSignInPromptData(error.data)
      ? error.data.signInUrl
      : null;

    const supportLink = (chunks: ReactNode) => (
      <BaseLinkOrButton
        href={
          isVirtualStore
            ? getVirtualBookstoreSupportLink()
            : submitVitalSourceSupport()
        }
        className="vst-link"
      >
        {chunks}
      </BaseLinkOrButton>
    );

    return (
      <DefaultTemplate title={title}>
        <PageTitle noHeading title={title} />
        <NoCoursesEmptyState
          signInUrl={institutionSignInUrl}
          courseBrowseEnabled={isVirtualStore}
          supportLink={supportLink}
        />
      </DefaultTemplate>
    );
  }

  throw error;
}
